import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/use-auth';
import { requestPushToken, onForegroundMessage, pushConfigured } from '@/lib/firebase';

const DEVICE_KEY = 'founderos_push_device';
const TOKEN_KEY = 'founderos_push_token';

const deviceLabel = () => {
  try {
    const ua = navigator.userAgent;
    const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Browser';
    const os = /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} on ${os}` : browser;
  } catch { return 'This device'; }
};

const computeDeviceEnabled = () => {
  try {
    return localStorage.getItem(DEVICE_KEY) === '1'
      && typeof Notification !== 'undefined'
      && Notification.permission === 'granted';
  } catch { return false; }
};

/**
 * Web push (FCM) registration. Tokens are PER DEVICE / PER BROWSER, so a new
 * device always needs `enable()` again. `deviceEnabled` reflects whether *this*
 * device is registered. Foreground messages surface as toasts.
 */
export function usePush() {
  const { token: authToken, API } = useAuth();
  const [deviceEnabled, setDeviceEnabled] = useState(computeDeviceEnabled);

  const enable = useCallback(async () => {
    const fcmToken = await requestPushToken();
    const res = await fetch(`${API}/api/push/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ token: fcmToken, platform: 'web', label: deviceLabel() }),
    });
    if (!res.ok) throw new Error('Could not register device for push.');
    try { localStorage.setItem(DEVICE_KEY, '1'); localStorage.setItem(TOKEN_KEY, fcmToken); } catch { /* ignore */ }
    setDeviceEnabled(true);
    return fcmToken;
  }, [API, authToken]);

  const currentToken = (() => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; } })();

  const listDevices = useCallback(async () => {
    const res = await fetch(`${API}/api/push/devices`, { headers: { Authorization: `Bearer ${authToken}` } });
    if (!res.ok) return [];
    const d = await res.json();
    const devices = d.devices || [];
    // Reconcile the localStorage "this device is enabled" flag against the
    // server's truth. If the current token has been dropped server-side
    // (user id change, admin purge, DeviceNotRegistered cleanup), clear the
    // flag so the UI shows "Enable on this device" again instead of a
    // permanently-disabled "Enabled" button.
    try {
      const savedToken = localStorage.getItem(TOKEN_KEY) || '';
      const stillRegistered = !!savedToken && devices.some((x) => x.token === savedToken);
      if (!stillRegistered) {
        localStorage.removeItem(DEVICE_KEY);
        localStorage.removeItem(TOKEN_KEY);
        setDeviceEnabled(false);
      }
    } catch { /* ignore */ }
    return devices;
  }, [API, authToken]);

  const removeDevice = useCallback(async (tkn) => {
    await fetch(`${API}/api/push/devices`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ token: tkn }),
    });
    if (tkn === currentToken) {
      try { localStorage.removeItem(DEVICE_KEY); localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
      setDeviceEnabled(false);
    }
  }, [API, authToken, currentToken]);

  // Auto-reconcile once per session so the "Get notified on this device"
  // nudge doesn't linger when the device is already registered server-side
  // (or vice versa: hide it if the current localStorage flag is a stale
  // orphan). Runs when the auth token becomes available.
  useEffect(() => {
    if (!authToken || !pushConfigured()) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/push/devices`, { headers: { Authorization: `Bearer ${authToken}` } });
        if (!res.ok) return;
        const d = await res.json();
        const devices = d.devices || [];
        if (cancelled) return;
        const savedToken = (() => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; } })();
        const permOk = typeof Notification !== 'undefined' && Notification.permission === 'granted';
        const stillRegistered = !!savedToken && devices.some((x) => x.token === savedToken);
        if (stillRegistered && permOk) {
          try { localStorage.setItem(DEVICE_KEY, '1'); } catch { /* ignore */ }
          setDeviceEnabled(true);
        } else if (!stillRegistered) {
          try { localStorage.removeItem(DEVICE_KEY); localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
          setDeviceEnabled(false);
        }
      } catch { /* network hiccups shouldn't crash the app */ }
    })();
    return () => { cancelled = true; };
  }, [authToken, API]);

  const sendTest = useCallback(async () => {
    const res = await fetch(`${API}/api/push/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    });
    return res.json();
  }, [API, authToken]);

  // Foreground messages → toast + a real OS notification (so it shows even when
  // the app tab is focused; otherwise users only see an in-app toast and think
  // push "didn't arrive" even though it was delivered).
  useEffect(() => {
    if (!pushConfigured()) return;
    let unsub = () => {};
    onForegroundMessage(async (payload) => {
      const n = payload?.notification || payload?.data || {};
      const d = payload?.data || {};
      const title = d.title || n.title || 'New notification';
      const body = d.body || n.body || '';
      toast.message(title, { description: body });
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && 'serviceWorker' in navigator) {
          // `getRegistration()` can resolve to undefined (page not yet controlled
          // by the SW); `ready` always resolves to the active registration once
          // the SW is installed — so the OS notification reliably shows.
          const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.ready);
          await reg.showNotification(title, {
            body, icon: d.icon || '/bower-icon.png', badge: '/bower-icon.png',
            data: d, tag: 'bower-fg', renotify: true, requireInteraction: true,
          });
        }
      } catch (err) {
        // Surface the real reason in the console instead of failing silently.
        console.warn('[push] foreground showNotification failed:', err);
      }
    }).then((fn) => { unsub = fn; });
    return () => unsub();
  }, []);

  return { enable, sendTest, configured: pushConfigured(), deviceEnabled, currentToken, listDevices, removeDevice };
}
